import {BrickSnake2000Statics} from "../../config/BrickSnake2000Statics";
import {gsap} from "gsap";
import {BrickSnake2000GameAssets} from "../../BrickSnake2000GameAssets";
import {BrickSnake2000} from "../../BrickSnake2000";
import {SpineGSAPWrapper} from "../../animation/SpineGSAPWrapper";
import {ReelView} from "../../../core/reel/simplereel/SimpleReelModel";

export enum BrickSnake2000SymbolStates {
    NORMAL, SPIN, WIN_TINY, WIN_SMALL, WIN_MEDIUM, WIN_LARGE, NO_WIN, LANDING,
}

export enum SYMBOL_NAME {
    WILD = "W", SCATTER = "S", M1 = "M1", M2 = "M2", M3 = "M3", M4 = "M4", L1 = "L1",
    L2 = "L2", L3 = "L3", L4 = "L4", XN = "XN", XS = "XS", MP = "MP", MN = "MN",
}

export interface SymbolArgs {
    symbolName:string;
    isSpinning:boolean;
    isStopping:boolean;
    reelsView:ReelView;
    reelPosition:number;
    reelID:number;
    symbolSize:number;
    suffix?:string;
}

export class BrickSnake2000Symbol extends PIXI.Container {
    protected view:PIXI.Container;
    protected mainSprite:PIXI.Sprite;
    protected spinSprite:PIXI.Sprite;
    protected winAnimation:PIXI.spine.Spine;
    protected reelsView:ReelView;
    protected winBlink:PIXI.Sprite;
    public symbolName:string;
    public reelPosition:number;
    public reelID:number;

    constructor(args:SymbolArgs) {
        //constructor(symbolName:string, isSpinning:boolean, reelsView:ReelView, reelPosition:number, reelID:number) {
        super();
        this.reelsView = args.reelsView;
        this.reelPosition = args.reelPosition;
        this.reelID = args.reelID;

        this.symbolName = args.symbolName;

        const symbolSuffix = args.suffix ? args.suffix : "";
        //const suffix = `${args.suffix != undefined ? args.suffix : ""}${args.isSpinning ? "_spin" : ""}`
        this.spinSprite = new PIXI.Sprite(BrickSnake2000.assetLoader.getTextures(BrickSnake2000GameAssets.getSymbolStateTexturePath(this.symbolName, symbolSuffix + "_spin"))[0])
        this.spinSprite.anchor.set(0.5,0.5);
        this.mainSprite = new PIXI.Sprite(BrickSnake2000.assetLoader.getTextures(BrickSnake2000GameAssets.getSymbolStateTexturePath(this.symbolName, symbolSuffix))[0]);
        this.mainSprite.anchor.set(0.5,0.5);

        this.spinSprite.alpha = args.isSpinning || args.isStopping ? 1 : 0;
        this.mainSprite.alpha = args.isSpinning || args.isStopping ? 0 : 1;

        const blinkName = this.symbolName == "M1"
                          ? "Sym_Blink/M1"
                          : this.symbolName == "MW"
                          ? "Sym_Blink/MW"
                          : this.symbolName[0] =="M"
                          ? "Sym_Blink/M"
                          : this.symbolName[0] =="L"
                          ? `Sym_Blink/${this.symbolName}`
                          : "Sym_Blink/M";
        this.winBlink = new PIXI.Sprite(BrickSnake2000.assetLoader.getTextures(blinkName)[0]);
        this.winBlink.anchor.set(0.5,0.5);
        this.winBlink.blendMode = PIXI.BLEND_MODES.ADD;
        this.winBlink.alpha = 0;

        this.winAnimation = new PIXI.spine.Spine(BrickSnake2000.assetLoader.getSpineAsset(BrickSnake2000GameAssets.SYMBOL_WIN));
        this.winAnimation.alpha = 0;

        this.view = new PIXI.Container();
        this.view.addChild(this.mainSprite);
        this.view.addChild(this.spinSprite);
        this.view.addChild(this.winBlink);
        this.view.addChild(this.winAnimation);

        this.addChild(this.view);
        //this.reelsView.layers.maskedNormal.addChild(this);
        this.moveToNormalLayer()
    }

    public crossFadeFromSpin(duration:number = 1):gsap.core.Timeline {
        return gsap.timeline()
            .add(gsap.to(this.mainSprite, {alpha: 1, duration: duration}))
            .add(gsap.to(this.spinSprite, {alpha: 0, duration: duration}), "<");
    }

    public crossFadeIntoSpin(duration:number = 1):gsap.core.Timeline {
        return gsap.timeline()
            .add(gsap.to(this.mainSprite, {alpha: 0, duration: duration}))
            .add(gsap.to(this.spinSprite, {alpha: 1, duration: duration}), "<");
    }

    public setNoWin():gsap.core.Timeline {
       return gsap.timeline().add(gsap.to(this, {alpha: 0.5, duration: 0.1}));
    }

    public setNormal():gsap.core.Timeline {
        return gsap.timeline().add(gsap.to(this, {alpha: 1.0, duration: 0.1}));
    }

    public resetState():gsap.core.Timeline {
        return gsap.timeline().add(() => {
            this.winAnimation.state.setEmptyAnimation(0, 0);
            this.winAnimation.alpha = 0;
            this.moveToNormalLayer();
            this.alpha = 1;
        })
    }

    public getWinBlink():gsap.core.Timeline {
        return gsap.timeline()
            .add(() => {this.winBlink.alpha = 1})
            .add(gsap.to(this.winBlink, {alpha: 0, duration: 0.08}), '>0.08');
    }

    public getWinAnimationBased():gsap.core.Timeline {
        return gsap.timeline()
            .add(() => {
                this.winAnimation.alpha = 1;
                this.moveToWinLayer();
            })
            .add(SpineGSAPWrapper.createTimeline(this.winAnimation, 0, `${this.symbolName}_Win`))
            .add(() => {
                this.winAnimation.alpha = 0;
                this.moveToNormalLayer();
            });
    }

    public getLandAnimation():gsap.core.Timeline {
        return gsap.timeline();
    }

    public moveToNormalLayer(object:PIXI.DisplayObject = this) {
        if(this.symbolName == "C") {
          this.reelsView.layers.maskedWinCap.addChild(object); // No need to sort since we can only have 1 wincap symbol
        } else if (this.symbolName != "M1") {
            this.reelsView.layers.maskedNormal.addChild(object);
            (<BrickSnake2000Symbol[]>this.reelsView.layers.maskedNormal.children).sort((a, b) => {
                return ((5 - a.reelID) * a.reelPosition) - ((5 - b.reelID) * b.reelPosition);
            })
        } else {
            this.reelsView.layers.maskedM1.addChild(object);
            (<BrickSnake2000Symbol[]>this.reelsView.layers.maskedM1.children).sort((a, b) => {
                return ((5 - a.reelID) * a.reelPosition) - ((5 - b.reelID) * b.reelPosition);
            })
        }
    }

    public moveToNormalOverLayer(object:PIXI.DisplayObject = this) {
        this.reelsView.layers.maskedOver.addChild(object);
        (<BrickSnake2000Symbol[]>this.reelsView.layers.maskedOver.children).sort((a, b) => {
            return ((5 - a.reelID) * a.reelPosition) - ((5 - b.reelID) * b.reelPosition);
        })
    }

    public moveToWinLayer(object:PIXI.DisplayObject = this) {
        this.reelsView.layers.win.addChild(object);
        (<BrickSnake2000Symbol[]>this.reelsView.layers.win.children).sort((a, b) => {
            return ((5 - a.reelID) * a.reelPosition) - ((5 - b.reelID) * b.reelPosition);
        })
    }
}